// src/components/Pages/Contact.jsx
import React, { useState, useEffect, useRef } from 'react';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaPaperPlane, FaExternalLinkAlt } from 'react-icons/fa';
import { motion } from 'framer-motion';
import './Contact.css';

// Free Map Component using OpenStreetMap (No API Key Required)
const ContactMap = () => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(false);
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);

  // Business coordinates (Buziga, Lukuli Road, Kampala, Uganda)
  const businessLocation = {
    lat:0.25605468645300933,
    lng: 32.619717,
    address: "Buziga, Lukuli Road, Kampala, Uganda",
    zoom: 16
  };

  useEffect(() => {
    let isMounted = true;

    const initMap = () => {
      if (!mapContainerRef.current || mapInstanceRef.current) return;
      if (!window.L) return;

      try {
        const map = window.L.map(mapContainerRef.current).setView([businessLocation.lat, businessLocation.lng], businessLocation.zoom);
        
        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 19
        }).addTo(map);
        
        const customIcon = window.L.divIcon({
          html: '<div style="font-size: 32px;">📍</div>',
          iconSize: [32, 32],
          className: 'custom-marker'
        });
        
        window.L.marker([businessLocation.lat, businessLocation.lng], { icon: customIcon })
          .addTo(map)
          .bindPopup(`
            <strong>Kajja Galleries</strong><br/>
            ${businessLocation.address}<br/>
            <a href="https://www.google.com/maps/dir/?api=1&destination=${businessLocation.lat},${businessLocation.lng}" target="_blank" style="color: maroon;">Get Directions →</a>
          `)
          .openPopup();
        
        window.L.control.scale().addTo(map);
        
        mapInstanceRef.current = map;
        
        setTimeout(() => {
          if (mapInstanceRef.current) {
            mapInstanceRef.current.invalidateSize();
          }
        }, 100);
        
        if (isMounted) setMapLoaded(true);
      } catch (error) {
        console.error('Error initializing map:', error);
        if (isMounted) setMapError(true);
      }
    };

    const loadLeaflet = () => {
      if (window.L) {
        initMap();
        return;
      }

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
      link.crossOrigin = '';
      document.head.appendChild(link);

      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
      script.crossOrigin = '';
      script.onload = () => initMap();
      script.onerror = () => {
        if (isMounted) setMapError(true);
      };
      document.head.appendChild(script);
    };

    loadLeaflet();

    return () => {
      isMounted = false;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  const getDirections = () => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${businessLocation.lat},${businessLocation.lng}`;
    window.open(url, '_blank');
  };

  if (mapError) {
    return (
      <div className="map-fallback" style={{ 
        height: '100%', 
        minHeight: '350px',
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#f5f5f5',
        borderRadius: '16px',
        padding: '20px',
        textAlign: 'center'
      }}>
        <FaMapMarkerAlt size={48} color="maroon" />
        <h3 style={{ margin: '16px 0 8px' }}>View Our Location</h3>
        <p style={{ marginBottom: '16px', color: '#666' }}>{businessLocation.address}</p>
        <button 
          onClick={getDirections}
          style={{
            background: 'maroon',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          Get Directions
        </button>
      </div>
    );
  }

  return (
    <div className="map-wrapper" style={{ position: 'relative', height: '100%', minHeight: '350px' }}>
      <div 
        ref={mapContainerRef}
        style={{ 
          height: '100%', 
          width: '100%', 
          borderRadius: '16px',
          overflow: 'hidden'
        }} 
      />
      {!mapLoaded && !mapError && (
        <div style={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f5f5f5',
          borderRadius: '16px'
        }}>
          <div className="loading-spinner4"></div>
          <span style={{ marginLeft: '10px' }}>Loading map...</span>
        </div>
      )}
    </div>
  );
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 3000);
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  return (
    <div className="contact-page">
      
      <motion.section 
        style={{
          position: "relative",
          height: "60vh",
          minHeight: "400px",
          backgroundImage: "url('/images/artbanner.jpg')",
          backgroundPosition: "center",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          color: "white",
        }}
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>Get in Touch With Us</h1>
          <p>We'd love to hear from you! Reach out for inquiries, collaborations, or just to say hello.</p>
        </div>
      </motion.section>

      {/* Contact Info & Map */}
      <section className="contact-container">
        <motion.div 
          className="contact-info"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 style={{color:'white'}}>Our Location</h2>
          <div className="info-card">
            <FaMapMarkerAlt className="icon" />
            <div>
              <h3>Address</h3>
              <p>Buziga, Lukuli Road, Kampala, Uganda</p>
            </div>
          </div>
          <div className="info-card">
            <FaPhone className="icon" />
            <div>
              <h3>Phone</h3>
              <p>+256 709 728 710</p>
              <p>+256 775 851 661</p>
            </div>
          </div>
          <div className="info-card">
            <FaEnvelope className="icon" />
            <div>
              <h3>Email</h3>
              <p>kajjagalleries@gmail.com</p>
            </div>
          </div>
          <div className="info-card">
            <FaClock className="icon" />
            <div>
              <h3>Working Hours</h3>
              <p>Mon - Fri: 9AM - 8PM</p>
              <p>Sat: 9AM - 6PM</p>
            </div>
          </div>
        </motion.div>

        {/* Interactive Map - Now Working without API Key */}
        <motion.div 
          className="map-container"
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <ContactMap />
        </motion.div>
      </section>

      {/* Contact Form */}
      <motion.section 
        className="contact-form-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="form-container">
          <h2>Send Us a Message</h2>
          {isSubmitted ? (
            <motion.div 
              className="success-message"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <FaPaperPlane className="success-icon" />
              <h3>Message Sent!</h3>
              <p>We'll get back to you soon.</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <textarea
                  name="message"
                  placeholder="Your Message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                ></textarea>
              </div>
              <motion.button
                type="submit"
                className='fbutton'
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Send Message
              </motion.button>
            </form>
          )}
        </div>
      </motion.section>
    </div>
  );
};

export default Contact;